import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuth } from '../../../we0-runtime/contexts/auth.context';
import {
  type AuthDialogOptions,
  notifyAuthDialogClose,
  registerAuthDialogOpener,
  unregisterAuthDialogOpener,
} from '../../lib/auth-dialog.store';
import { useI18n } from '../../../we0-runtime/i18n';

interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultMode?: 'login' | 'register';
}

export const AuthDialog: React.FC<AuthDialogProps> = ({
  open,
  onOpenChange,
  defaultMode = 'login',
}) => {
  const [mode, setMode] = useState<'login' | 'register'>(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hint, setHint] = useState('');
  const onSuccessRef = useRef<(() => void) | undefined>(undefined);

  const { signIn, signUp } = useAuth();
  const { t } = useI18n();

  // 注册全局 opener，允许非 React 代码（如 fetcher）触发弹窗
  useEffect(() => {
    registerAuthDialogOpener((opts?: AuthDialogOptions) => {
      setHint(opts?.message || '');
      setError('');
      setSuccess('');
      if (opts?.mode) setMode(opts.mode);
      onSuccessRef.current = opts?.onSuccess;
      onOpenChange(true);
    });
    return () => unregisterAuthDialogOpener();
  }, [onOpenChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'register') {
        if (password !== confirmPassword) {
          setError(t('auth.passwordMismatch'));
          setLoading(false);
          return;
        }

        const { error } = await signUp(email, password);
        if (error) {
          setError(error.message);
        } else {
          setSuccess('注册成功，请使用邮箱和密码登录。');
          setTimeout(() => {
            setMode('login');
            setSuccess('');
          }, 2000);
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error.message);
        } else {
          setSuccess(t('auth.loginSuccess'));
          setTimeout(() => {
            onOpenChange(false);
            resetForm();
            onSuccessRef.current?.();
          }, 800);
        }
      }
    } catch (err: any) {
      setError(err.message || t('auth.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    setHint('');
  };

  const selectMode = (nextMode: 'login' | 'register') => {
    if (nextMode === mode) return;
    setMode(nextMode);
    resetForm();
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      resetForm();
      notifyAuthDialogClose();
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="auth-dialog-content sm:max-w-[425px]">
          <div className="auth-dialog-panel">
            <DialogHeader className="auth-dialog-header">
              <span className="auth-dialog-kicker">GROWDEEP / WORKSPACE</span>
              <DialogTitle className="auth-dialog-title">
                {mode === 'login' ? '登录工作台' : '创建工作台账户'}
              </DialogTitle>
              <DialogDescription className="auth-dialog-description">
                {mode === 'login'
                  ? '登录后查看属于你的品牌增长数据与服务进度。'
                  : '仅需邮箱和密码，即可创建你的工作台账户。'}
              </DialogDescription>
            </DialogHeader>

            <div className="auth-dialog-tabs" role="tablist" aria-label="账户操作">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'login'}
                className={mode === 'login' ? 'is-active' : ''}
                onClick={() => selectMode('login')}
                disabled={loading}
              >
                登录
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'register'}
                className={mode === 'register' ? 'is-active' : ''}
                onClick={() => selectMode('register')}
                disabled={loading}
              >
                注册
              </button>
            </div>

        {hint && (
          <div className="auth-dialog-hint">
            {hint}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-dialog-form">
          <div className="auth-dialog-field">
            <Label htmlFor="dialog-email">邮箱</Label>
            <div className="auth-dialog-input-wrap">
              <Input
                id="dialog-email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="auth-dialog-field">
            <Label htmlFor="dialog-password">密码</Label>
            <div className="auth-dialog-input-wrap">
              <Input
                id="dialog-password"
                type="password"
                placeholder="至少 6 位"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                disabled={loading}
              />
            </div>
          </div>

          {mode === 'register' && (
            <div className="auth-dialog-field">
              <Label htmlFor="dialog-confirmPassword">确认密码</Label>
              <div className="auth-dialog-input-wrap">
                <Input
                  id="dialog-confirmPassword"
                  type="password"
                  placeholder="再次输入密码"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {error && <div className="auth-dialog-feedback is-error">{error}</div>}
          {success && <div className="auth-dialog-feedback is-success">{success}</div>}

          <Button type="submit" className="auth-dialog-submit" disabled={loading}>
            <span>{loading ? '正在处理…' : mode === 'login' ? '登录工作台' : '创建账户'}</span>
            <span aria-hidden="true">↗</span>
          </Button>

          <div className="auth-dialog-switch">
            <span>{mode === 'login' ? '还没有账户？' : '已经有账户？'}</span>
            <Button
              type="button"
              variant="ghost"
              onClick={() => selectMode(mode === 'login' ? 'register' : 'login')}
              disabled={loading}
            >
              {mode === 'login' ? '去注册' : '返回登录'}
            </Button>
          </div>
        </form>
          </div>
        </DialogContent>
    </Dialog>
  );
};
